import React from 'react';
import AppActionMenu from '../../../../components/AppActionMenu';

export default function LeaveRuleDetailsTable({
  records = [],
  isLoading = false,
  onEdit,
  onDelete,
  onAddNew,
}) {
  if (isLoading) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="h-8 w-8 animate-spin rounded-full border-3 border-primary border-t-transparent" />
          <p className="text-xs font-semibold text-on-surface-variant">Loading Leave Quotas...</p>
        </div>
      </div>
    );
  }

  if (records.length === 0) {
    return (
      <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest p-12 text-center shadow-sm">
        <div className="flex flex-col items-center justify-center gap-3">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 text-primary">
            <span className="material-symbols-outlined text-3xl">playlist_add</span>
          </div>
          <h3 className="text-lg font-bold text-on-surface">No Leave Quotas Configured</h3>
          <p className="text-xs text-on-surface-variant max-w-md">
            Click &quot;Add Leave Quota&quot; to define allowed days and carry forward rules for specific leave categories.
          </p>
          {onAddNew && (
            <button
              type="button"
              onClick={onAddNew}
              className="mt-2 rounded-xl bg-primary px-5 py-2 text-xs font-bold text-on-primary shadow-sm hover:bg-primary/90 transition cursor-pointer"
            >
              Add Leave Quota
            </button>
          )}
        </div>
      </div>
    );
  }

  const getGenderBadge = (gender) => {
    const g = String(gender || 'A').toUpperCase();
    if (g === 'M') {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-blue-500/10 px-2 py-0.5 text-[11px] font-bold text-blue-600">
          <span className="material-symbols-outlined text-xs">male</span>
          Male
        </span>
      );
    }
    if (g === 'F') {
      return (
        <span className="inline-flex items-center gap-1 rounded-md bg-pink-500/10 px-2 py-0.5 text-[11px] font-bold text-pink-600">
          <span className="material-symbols-outlined text-xs">female</span>
          Female
        </span>
      );
    }
    return (
      <span className="inline-flex items-center gap-1 rounded-md bg-surface-container-high px-2 py-0.5 text-[11px] font-bold text-on-surface-variant">
        All
      </span>
    );
  };

  return (
    <div className="overflow-hidden rounded-2xl border border-outline-variant/25 bg-surface-container-lowest shadow-sm">
      <div className="overflow-x-auto">
        <table className="w-full min-w-[1050px] text-left text-sm">
          <thead>
            <tr className="border-b border-outline-variant/20 bg-surface-container-low/60 text-xs font-bold uppercase tracking-[0.08em] text-on-surface-variant">
              <th className="px-5 py-3.5">Leave Type</th>
              <th className="px-5 py-3.5">Gender</th>
              <th className="px-5 py-3.5">Total Days</th>
              <th className="px-5 py-3.5">Min Days</th>
              <th className="px-5 py-3.5">Max Days</th>
              <th className="px-5 py-3.5">CF Days</th>
              <th className="px-5 py-3.5">Accumulate</th>
              <th className="px-5 py-3.5">Lapse Days</th>
              <th className="px-5 py-3.5">Extra Days</th>
              <th className="px-5 py-3.5">Sandwich Rule</th>
              <th className="w-20 px-5 py-3.5 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-outline-variant/15">
            {records.map((item) => (
              <tr
                key={item.id}
                className="transition-colors hover:bg-surface-container-low/50 group"
              >
                {/* Leave Type with color pill & alias */}
                <td className="px-5 py-3.5 font-semibold text-on-surface">
                  <div className="flex items-center gap-2.5">
                    <span
                      className="h-3 w-3 shrink-0 rounded-full border border-black/10 shadow-2xs"
                      style={{ backgroundColor: item.leaveTypeColor || '#3B82F6' }}
                    />
                    <span className="font-bold text-on-surface">{item.leaveTypeName}</span>
                    {item.leaveTypeAlias && (
                      <span className="rounded bg-surface-container-high px-1.5 py-0.5 text-[10px] font-bold text-on-surface-variant">
                        {item.leaveTypeAlias}
                      </span>
                    )}
                  </div>
                </td>

                {/* Gender */}
                <td className="px-5 py-3.5">
                  {getGenderBadge(item.gender)}
                </td>

                {/* Total Days */}
                <td className="px-5 py-3.5 text-xs font-bold text-on-surface">
                  {Number(item.totalDays).toFixed(2)}
                </td>

                {/* Min Days */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  {Number(item.minDays).toFixed(2)}
                </td>

                {/* Max Days */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  {Number(item.maxDays).toFixed(2)}
                </td>

                {/* CF Days */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  {Number(item.cfDays).toFixed(2)}
                </td>

                {/* Accumulate */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  <span
                    className={`inline-flex items-center rounded-md px-2 py-0.5 text-[11px] font-bold ${
                      item.accumulate === 'Yes'
                        ? 'bg-emerald-500/10 text-emerald-600'
                        : 'bg-surface-container-high text-on-surface-variant'
                    }`}
                  >
                    {item.accumulate || 'No'}
                  </span>
                </td>

                {/* Lapse Days */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  {Number(item.lapseDays).toFixed(2)}
                </td>

                {/* Extra Days */}
                <td className="px-5 py-3.5 text-xs font-medium text-on-surface">
                  {Number(item.extraDays).toFixed(2)}
                </td>

                {/* Sandwich Rule */}
                <td className="px-5 py-3.5">
                  {item.sandwichPresent ? (
                    <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/10 px-2 py-0.5 text-[11px] font-bold text-emerald-600">
                      <span className="material-symbols-outlined text-xs">check</span>
                      Yes
                    </span>
                  ) : (
                    <span className="inline-flex items-center rounded-md bg-surface-container-high px-2 py-0.5 text-[11px] font-semibold text-on-surface-variant">
                      No
                    </span>
                  )}
                </td>

                {/* Actions */}
                <td
                  className="px-5 py-3.5 text-right"
                  onClick={(e) => e.stopPropagation()}
                >
                  <AppActionMenu
                    triggerLabel=""
                    triggerIcon="more_vert"
                    showChevron={false}
                    triggerClassName="rounded-lg p-1.5 text-outline-variant transition-colors hover:bg-surface-container-low hover:text-primary bg-transparent cursor-pointer"
                    items={[
                      { key: 'edit', label: 'Edit Quota', icon: 'edit' },
                      { key: 'delete', label: 'Remove Quota', icon: 'delete', danger: true },
                    ]}
                    onSelect={(key) => {
                      if (key === 'edit') {
                        onEdit?.(item);
                      } else if (key === 'delete') {
                        onDelete?.(item.id, item.leaveTypeName);
                      }
                    }}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
